import { ds } from '@/components/lib/design.system';
import { AppColors } from '@/components/lib/theme';
import { designSystem } from '@/styles/styles';
import { EventStatus, Outcome, Result, VoidType } from '@/types/api.generated';
import { ParseKeys } from 'i18next';

export const PickStatus = {
    UPCOMING: 'upcoming',
    IN_PROGRESS_MORE: 'in_progress_more',
    IN_PROGRESS_LESS: 'in_progress_less',
    CORRECT: 'correct',
    INCORRECT: 'incorrect',
    VOID: 'void',
    REVIVED: 'revived',
    RESCUED: 'rescued',
    GHOSTED: 'ghost',
    TIE: 'tie',
} as const;

export type PickStatus = (typeof PickStatus)[keyof typeof PickStatus];

export type PickStatusInput = {
    result?: Result | null;
    voidType?: VoidType | null;
    eventStatus?: EventStatus | null;
    outcome?: Outcome | null;
};

// Manual and NotPlayed are considered plain/standard voids.
const VOID_TYPE_STATUS: Record<VoidType, PickStatus> = {
    [VoidType.Manual]: PickStatus.VOID,
    [VoidType.NotPlayed]: PickStatus.VOID,
    /** @deprecated split into MANUAL and NOT_PLAYED; still sent to older app versions */
    [VoidType.Standard]: PickStatus.VOID,
    [VoidType.Tie]: PickStatus.TIE,
    [VoidType.Revive]: PickStatus.REVIVED,
    [VoidType.Rescue]: PickStatus.RESCUED,
    [VoidType.Ghost]: PickStatus.GHOSTED,
};

/**
 * The only place `result` / `voidType` / `eventStatus` are branched on.
 *
 * A void with no type, or one this app version doesn't know, is a plain void — the
 * safe default, since it settles the pick rather than leaving it looking live.
 */
export const getPickStatus = ({ result, voidType, eventStatus, outcome }: PickStatusInput): PickStatus => {
    if (!result) {
        if (eventStatus !== EventStatus.InProgress) {
            return PickStatus.UPCOMING;
        }
        return outcome === Outcome.Less ? PickStatus.IN_PROGRESS_LESS : PickStatus.IN_PROGRESS_MORE;
    }
    if (result === Result.Win) {
        return PickStatus.CORRECT;
    }
    if (result === Result.Loss) {
        return PickStatus.INCORRECT;
    }
    return (voidType && VOID_TYPE_STATUS[voidType]) ?? PickStatus.VOID;
};

export const isPickInProgress = (status: PickStatus) =>
    status === PickStatus.IN_PROGRESS_MORE || status === PickStatus.IN_PROGRESS_LESS;

export const shouldFillBar = (result: Result | undefined, outcome: Outcome, voidType?: VoidType) => {
    return (
        (result === Result.Win && outcome === Outcome.More) ||
        (result === Result.Loss && outcome === Outcome.Less) ||
        (result === Result.Void && voidType === VoidType.Tie)
    );
};

// A settled win or loss whose value says the opposite (ex: a pick WIN - outcome MORE with value 15 - but currentValue < 15)
export const isValueOutOfSyncWithResult = ({
    result,
    outcome,
    value,
    max,
}: {
    result: Result | undefined;
    outcome: Outcome;
    value: number | null | undefined;
    max: number;
}) => {
    if (value == null || (result !== Result.Win && result !== Result.Loss)) {
        return false;
    }
    return shouldFillBar(result, outcome) ? value < max : value > max;
};

type SeparatorLabel = {
    i18nKey: ParseKeys<['pickem_entries', 'common']>;
    color: AppColors;
    testID: string;
};

type PickStatusConfig = {
    /** Raw color for the progress bar fill and the entry pick dots. */
    barColor: string;
    /** Theme color for the ring around the player avatar. */
    avatarRingColor: AppColors;
    /** Theme color for the status badge under the player avatar. */
    badgeColor: AppColors;
    /** The bar fill uses `barColor` rather than the neutral track. */
    hasColoredFill?: boolean;
    /** Replaces the bar separator, which is otherwise a plain vertical line. */
    separator?: SeparatorLabel;
};

export const PICK_STATUS_CONFIG: Record<PickStatus, PickStatusConfig> = {
    [PickStatus.UPCOMING]: {
        barColor: designSystem.colors.neutral700,
        avatarRingColor: 'neutral500',
        badgeColor: ds.colorToken.background.neutral.inverse,
    },
    [PickStatus.IN_PROGRESS_MORE]: {
        barColor: ds.colorValue.background.neutral.inverse,
        avatarRingColor: 'neutral500',
        badgeColor: ds.colorToken.background.neutral.inverse,
    },
    [PickStatus.IN_PROGRESS_LESS]: {
        barColor: ds.colorValue.background.neutral.inverse,
        avatarRingColor: 'neutral500',
        badgeColor: ds.colorToken.background.neutral.inverse,
    },
    [PickStatus.CORRECT]: {
        barColor: ds.colorValue.background.accent.green.strong,
        avatarRingColor: ds.colorToken.background.accent.green.strong,
        badgeColor: ds.colorToken.background.accent.green.strong,
        hasColoredFill: true,
    },
    [PickStatus.INCORRECT]: {
        barColor: ds.colorValue.background.accent.rose.strong,
        avatarRingColor: ds.colorToken.background.accent.rose.strong,
        badgeColor: ds.colorToken.background.accent.rose.strong,
        hasColoredFill: true,
    },
    [PickStatus.VOID]: {
        barColor: designSystem.colors.neutral400,
        avatarRingColor: ds.colorToken.background.neutral.stronger,
        badgeColor: ds.colorToken.background.neutral.stronger,
        separator: { i18nKey: 'common:voided', color: 'neutral400', testID: 'voided-separator-text' },
    },
    [PickStatus.REVIVED]: {
        barColor: ds.colorValue.background.accent.purple.strong,
        avatarRingColor: ds.colorToken.background.accent.purple.strong,
        badgeColor: ds.colorToken.background.accent.purple.strong,
        hasColoredFill: true,
        separator: {
            i18nKey: 'pickem_entries:revived',
            color: ds.colorToken.text.primary,
            testID: 'revived-separator-text',
        },
    },
    [PickStatus.RESCUED]: {
        barColor: ds.colorValue.background.accent.indigo.strong,
        avatarRingColor: ds.colorToken.background.accent.indigo.strong,
        badgeColor: ds.colorToken.background.accent.indigo.strong,
        hasColoredFill: true,
        separator: {
            i18nKey: 'pickem_entries:rescued',
            color: ds.colorToken.text.primary,
            testID: 'rescued-separator-text',
        },
    },
    [PickStatus.GHOSTED]: {
        barColor: ds.colorValue.background.neutral.stronger,
        avatarRingColor: ds.colorToken.background.neutral.stronger,
        badgeColor: ds.colorToken.background.neutral.stronger,
        hasColoredFill: true,
        separator: {
            i18nKey: 'pickem_entries:ghosted',
            color: ds.colorToken.text.primary,
            testID: 'ghosted-separator-text',
        },
    },
    [PickStatus.TIE]: {
        barColor: ds.colorValue.background.accent.orange.strong,
        avatarRingColor: ds.colorToken.background.accent.orange.strong,
        badgeColor: ds.colorToken.background.accent.orange.strong,
        hasColoredFill: true,
    },
};

export const getPickStatusConfig = (input: PickStatusInput) => PICK_STATUS_CONFIG[getPickStatus(input)];

export const getStatusColor = (result?: Result, voidType?: VoidType, eventStatus?: EventStatus) =>
    getPickStatusConfig({ result, voidType, eventStatus }).barColor;
